import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { X, Users, DollarSign, Calendar, FileText, ArrowRight, Sparkles, ListTodo, MapPin, Wallet } from "lucide-react";

/**
 * WelcomeModal — shown once per user (localStorage-flagged) after their first
 * successful login. Content adapts to the user's role.
 */
export default function WelcomeModal() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!user?.id) return;
        const key = `hrm_welcomed_${user.id}`;
        if (localStorage.getItem(key) !== "1") {
            setOpen(true);
        }
    }, [user?.id]);

    const dismiss = () => {
        if (user?.id) localStorage.setItem(`hrm_welcomed_${user.id}`, "1");
        setOpen(false);
    };

    if (!open || !user) return null;

    const cards = pickCards(user.role);
    const name = (user.name || user.email || "").split(" ")[0] || "there";

    return (
        <div className="fixed inset-0 z-[70] bg-ink/60 flex items-center justify-center p-4" data-testid="welcome-modal">
            <div className="bg-bone w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
                <div className="relative bg-klein text-white px-8 py-10">
                    <button onClick={dismiss} data-testid="welcome-close-btn"
                        className="absolute top-4 right-4 text-white/70 hover:text-white" aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                    <Sparkles className="w-6 h-6 text-white/80" />
                    <div className="overline !text-white/70 mt-3">Welcome aboard</div>
                    <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
                        Hi {name}, let&apos;s get you set up.
                    </h2>
                    <p className="mt-3 text-sm text-white/80 max-w-lg">
                        Suntosh HRM handles payroll, leave, loans, tasks and attendance for your team.
                        Here are a few things to get started with. You can always come back later.
                    </p>
                </div>
                <div className="p-6 sm:p-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {cards.map((c, i) => (
                            <Link key={i} to={c.to} onClick={dismiss} data-testid={`welcome-card-${c.testid}`}
                                className="group border border-ink/10 bg-white p-5 hover:border-klein transition-colors">
                                <div className="w-9 h-9 bg-klein/10 text-klein flex items-center justify-center">
                                    <c.Icon className="w-5 h-5" />
                                </div>
                                <div className="mt-3 font-display font-bold text-lg text-ink">{c.title}</div>
                                <div className="mt-1 text-xs text-ink/60 leading-relaxed">{c.blurb}</div>
                                <div className="mt-3 text-xs uppercase tracking-[0.15em] text-klein inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                    {c.cta} <ArrowRight className="w-3 h-3" />
                                </div>
                            </Link>
                        ))}
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button onClick={dismiss} data-testid="welcome-dismiss-btn"
                            className="text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-klein">
                            Skip for now →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function pickCards(role) {
    if (role === "admin" || role === "hr") {
        return [
            { Icon: Users, title: "Add your team", blurb: "Onboard employees — we'll auto-email them an activation link for their self-service portal.", to: "/employees", cta: "Add employees", testid: "add-employees" },
            { Icon: DollarSign, title: "Run your first payroll", blurb: "Automated PAYE (Nigeria Tax Act 2026), pension, loans and payslips — all in one click.", to: "/payroll", cta: "Open payroll", testid: "payroll" },
            { Icon: Calendar, title: "Approve leave requests", blurb: "Track balances, approve time off, and log everything to attendance automatically.", to: "/pending-approvals", cta: "Review requests", testid: "approvals" },
            { Icon: FileText, title: "Configure your brand", blurb: "Set your logo, colours, public email and salary structure — appears on every payslip and letter.", to: "/settings", cta: "Open settings", testid: "settings" },
        ];
    }
    if (role === "department_lead") {
        return [
            { Icon: Users, title: "Your team at a glance", blurb: "See only your department's headcount, celebrations and performance.", to: "/dashboard", cta: "Open dashboard", testid: "dashboard" },
            { Icon: ListTodo, title: "Assign daily tasks", blurb: "Delegate work and track completion — feeds into your team's Total Performance Tracker.", to: "/tasks", cta: "Open tasks", testid: "tasks" },
            { Icon: Calendar, title: "Approve leave & loans", blurb: "First-line approver for requests from your department.", to: "/pending-approvals", cta: "Review requests", testid: "approvals" },
            { Icon: MapPin, title: "Approve offsite work", blurb: "Client sites, remote days, field visits — reflected on attendance and payslip.", to: "/offsite", cta: "Open offsite", testid: "offsite" },
        ];
    }
    // employee / disbursement / other → simpler card set
    return [
        { Icon: Wallet, title: "Your payslips & balances", blurb: "Download payslips and see your leave, loan and advance balances anytime.", to: "/dashboard", cta: "Open dashboard", testid: "dashboard" },
        { Icon: Calendar, title: "Apply for leave", blurb: "File a leave request — you'll get notified as it moves through approval.", to: "/leaves", cta: "Apply", testid: "leaves" },
        { Icon: MapPin, title: "Working offsite?", blurb: "Client visit or working from home? File an offsite request to keep attendance accurate.", to: "/offsite", cta: "New offsite", testid: "offsite" },
        { Icon: ListTodo, title: "Today's tasks", blurb: "See what's assigned to you and mark items done as you go.", to: "/tasks", cta: "My tasks", testid: "tasks" },
    ];
}
